import { isString } from '@sgme/fp';

import type { WebapiEndpointBuilder } from '@/api/webapi.ts';
import { logger } from '@/components/SgwtBootstrap/logger.ts';
import type { FixingMarginType, FixingPriceType } from '@/store/bulkOrder/state.ts';
import type { SubmitOrderResponse, ValidationResponseDetailContent } from '@/store/orders/state.ts';
import type { ClientWay } from '@/store/orders/types.ts';
import type { CurrencyIndex } from '@/utils/currencies/types.ts';

export type UpdateFixingRequest = {
  tileId: string;

  orderRef: string;
  way: ClientWay;
  currency1: string;
  currency2: string;
  notional?: number;
  notionalCurrencyIndex: CurrencyIndex;

  fixingReference: string;
  fixingDateUtc: string | undefined;
  fixingPlace?: string;
  fixingTime?: string;

  fixingPriceType?: FixingPriceType;
  commercialMargin: number | undefined;
  fixingMarginType: FixingMarginType | undefined;
};

export type UpdateFixingBody = {
  orderRef: string;
  amountInCcy1?: number;
  amountInCcy2?: number;
  ccyPair: string; // 'EUR/USD';
  way: ClientWay;
  fixingPriceType: FixingPriceType | undefined;
  marginInBps: number | undefined;
  fixingMarginType: FixingMarginType | undefined;
  fixingBenchmark: string;
  fixingDateUtc: string | undefined;
  fixingPlace?: string;
  fixingTime?: string;
};

export const updateFixing = (builder: WebapiEndpointBuilder) => {
  return builder.mutation<SubmitOrderResponse, UpdateFixingRequest>({
    query: (request) => ({
      url: '/oms/update/Fixing',
      method: 'POST',
      body: generateBody(request),
      headers: {
        // without this header, the response is a string
        // instead of JSON, even if the value is "application/json"
        accept: '*/*',
      },
    }),
    transformResponse: (
      response:
        | string
        | {
            validationResponseDetailContent: ValidationResponseDetailContent[];
          },
    ) => {
      return {
        type: 'success',
        orderId: response as string, // assuming the response is a string with the order ID
      };
    },
    transformErrorResponse: (errorResponse, _meta, arg): SubmitOrderResponse => {
      const tileId = arg.tileId;
      if (errorResponse.status === 'TIMEOUT_ERROR' || errorResponse.status === 504) {
        return {
          type: 'failure',
          errors: [
            {
              field: 'unknown',
              errorDescription: 'timeout',
              errorCode: -1,
            },
          ],
        };
      }

      const data = errorResponse.data as
        | string
        | {
            validationResponseDetailContent: ValidationResponseDetailContent[];
          };

      if (typeof errorResponse.status === 'number' && errorResponse.status >= 400 && errorResponse.status <= 499) {
        logger.logError(
          `Order submission failed because of a validation field rejection. orderId: {tileId_s},
            response: {data_s}`,
          tileId,
          JSON.stringify(data),
        );
        if (isValidateOrderResponse(data)) {
          return {
            type: 'failure',
            errors: data?.validationResponseDetailContent,
          };
        }

        if (isString(data)) {
          return {
            type: 'failure',
            errors: [
              {
                field: 'unknown',
                errorDescription: data,
                errorCode: -1,
              },
            ],
          };
        }
      }

      if (typeof errorResponse.status === 'number' && errorResponse.status >= 500 && errorResponse.status <= 599) {
        logger.logError(
          'Backend crashed with error 500 for orderId: {tileId_s}, response: {data_s}',
          tileId,
          JSON.stringify(data),
        );
        return {
          type: 'failure',
          errors: [
            {
              field: 'unknown',
              errorDescription: 'server.error',
              errorCode: -1,
            },
          ],
        };
      }

      logger.logError(
        'Backend returned an unexpected error {status_s} for orderId: {tileId_s}, response: {data_s}',
        errorResponse.status,
        tileId,
        JSON.stringify(data),
      );

      return {
        type: 'failure',
        errors: [
          {
            field: 'unknown',
            errorDescription: 'order.unexpected',
            errorCode: -1,
          },
        ],
      };
    },
  });
};

const generateBody = (order: UpdateFixingRequest): UpdateFixingBody => ({
  orderRef: order.orderRef ?? '',
  ccyPair: `${order.currency1}/${order.currency2}`,
  way: order.way ?? 'buy',
  amountInCcy1: order.notionalCurrencyIndex === 1 ? order.notional : undefined,
  amountInCcy2: order.notionalCurrencyIndex === 2 ? order.notional : undefined,
  fixingPriceType: order.fixingPriceType,
  fixingMarginType: order.fixingMarginType ? order.fixingMarginType : undefined,
  marginInBps: order.commercialMargin ? order.commercialMargin : undefined,
  fixingBenchmark: order.fixingReference,
  fixingDateUtc: order.fixingDateUtc,
  fixingPlace: order.fixingPlace,
  fixingTime: order.fixingTime,
});

function isValidateOrderResponse(response: unknown): response is {
  validationResponseDetailContent: ValidationResponseDetailContent[];
  isReadyToSubmit: boolean;
} {
  return (
    typeof response === 'object' &&
    response !== null &&
    'validationResponseDetailContent' in response &&
    'isReadyToSubmit' in response &&
    Array.isArray(response.validationResponseDetailContent)
  );
}
